import { Injectable, Injector, computed, inject } from '@angular/core';
import { Observable, filter, from, map, switchMap } from 'rxjs';
import { toObservable } from '@angular/core/rxjs-interop';
import { NavigationService, ScreenRecordingService } from '@libs/shared-services';

@Injectable({ providedIn: 'root' })
export class CollaborationScreenRecordingService {
  private readonly screenRecordingService = inject(ScreenRecordingService);
  private readonly navigationService = inject(NavigationService);
  private readonly injector = inject(Injector);

  public readonly recordingStatus = this.screenRecordingService.recordingStatus;
  public activeDeviceId = this.screenRecordingService.activeDeviceId;
  public readonly mediaVolumeLevel = this.screenRecordingService.mediaVolumeLevel;
  public readonly micHasNoVolume = this.screenRecordingService.micHasNoVolume;

  public readonly isNotRecording = computed(() => this.screenRecordingService.recordingStatus() === 'stopped');

  private collaborationId: string | null = null;
  private siteId: string | null = null;
  private patientId: string | null = null;

  // eslint-disable-next-line rxjs/finnish
  public startRecording(collaborationId: string, siteId: string, patientId: string | null = null): Observable<Blob | null> {
    this.collaborationId = collaborationId;
    this.siteId = siteId;
    this.patientId = patientId;
    return from(this.screenRecordingService.openExplainScreenRecordingModal()).pipe(
      switchMap(() =>
        toObservable(this.isNotRecording, { injector: this.injector }).pipe(
          filter((isNotRecording) => !!isNotRecording),
          map(() => this.getBlob()),
        ),
      ),
    );
  }

  public stopRecording(): void {
    this.screenRecordingService.stopRecording();
    if (!this.collaborationId) {
      return;
    }

    if (this.siteId && this.patientId) {
      this.navigationService.navigate('collabOnSiteForPatient', false, [this.siteId, this.patientId], {
        collaborationId: this.collaborationId,
      });
    } else if (this.siteId) {
      this.navigationService.navigate('allCollaborationsOnSite', false, [this.siteId], { collaborationId: this.collaborationId });
    }

    this.collaborationId = null;
    this.siteId = null;
    this.patientId = null;
  }

  public getBlob(): Blob | null {
    return this.screenRecordingService.getBlob();
  }
}
